type LayoutLink = {
  text?: string;
  url?: string;
  href?: string;
  order?: number;
  style?: string;
  category?: string;
  [key: string]: unknown;
};

type LayoutComponent = {
  type?: string;
  data?: {
    links?: LayoutLink[];
    navigation?: LayoutLink[];
    navItems?: LayoutLink[];
    menuItems?: LayoutLink[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export function pagePath(slug: string, isHomePage: boolean): string {
  return isHomePage ? '/' : `/${slug}`;
}

export function normalizeLayoutComponents(raw: unknown): LayoutComponent[] {
  if (Array.isArray(raw)) return raw as LayoutComponent[];
  if (typeof raw === 'string') {
    try {
      return normalizeLayoutComponents(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (
    raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as { components?: unknown }).components)
  ) {
    return (raw as { components: LayoutComponent[] }).components;
  }
  return [];
}

function readLinks(data: LayoutComponent['data']): LayoutLink[] {
  const links = data?.links || data?.navigation || data?.navItems || data?.menuItems;
  return Array.isArray(links) ? links : [];
}

function linkUrl(link: LayoutLink): string {
  return (
    (typeof link.url === 'string' && link.url) || (typeof link.href === 'string' && link.href) || ''
  );
}

function hasUrl(links: LayoutLink[], url: string): boolean {
  return links.some(link => linkUrl(link) === url);
}

function withLink(component: LayoutComponent, link: LayoutLink): LayoutComponent {
  const data = component.data || {};
  const links = readLinks(data);
  if (hasUrl(links, link.url || '')) return component;
  const next = [...links, link];
  const nextData: NonNullable<LayoutComponent['data']> = { ...data, links: next };
  if (Array.isArray(data.navigation)) nextData.navigation = next;
  if (Array.isArray(data.navItems)) nextData.navItems = next;
  if (Array.isArray(data.menuItems)) nextData.menuItems = next;
  return { ...component, data: nextData };
}

export function appendPageLinksToLayout(
  components: unknown,
  options: {
    title: string;
    url: string;
    addToNav: boolean;
    addToFooter: boolean;
  }
): LayoutComponent[] {
  const list = normalizeLayoutComponents(components);
  if (!options.addToNav && !options.addToFooter) return list;

  return list.map(component => {
    if (options.addToNav && (component.type === 'navbar' || component.type === 'navigation-menu')) {
      const links = readLinks(component.data);
      return withLink(component, {
        text: options.title,
        url: options.url,
        order: links.length,
        style: 'normal',
      });
    }

    if (options.addToFooter && component.type === 'footer') {
      const links = readLinks(component.data);
      const lastCategory = links[links.length - 1]?.category;
      return withLink(component, {
        text: options.title,
        url: options.url,
        category: lastCategory || 'General',
      });
    }

    return component;
  });
}
